// src/components/ProtectedRoute/Admin/Dialogs/ScientificName/DialogScientificNameEditor.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
  Autocomplete,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import supabase from "../../../../utils/supabase";
import { selectInBatched } from "../../../../utils/supabase-batch";
import BaseDialog from "./Framework/BaseDialog";
import { useSupabaseSession } from "../../../../utils/AuthSessionProvider";
import { useMasterData } from "../../../../utils/MasterDataProvider";

import SectionIdSource from "./Sections/SectionIdSource";
import SectionNameSpelling from "./Sections/SectionNameSpelling";
import SectionZooBank from "./Sections/SectionZooBank";
import SectionAuthority from "./Sections/SectionAuthority";
import SectionTaxonomy from "./Sections/SectionTaxonomy";
import SectionTypeSpecimen from "./Sections/SectionTypeSpecimen";
import SectionTypeTaxon from "./Sections/SectionTypeTaxon";
import SectionOtherInfo from "./Sections/SectionOtherInfo";
import SectionExtantFossil from "./Sections/SectionExtantFossil";
import TypeHostSelector from "./parts/TypeHostSelector";
import useFetchScientificNames from "../myHooks/useFetchScientificNames";
import ScientificNameSelector from "./parts/ScientificNameSelector";
import DialogTaxonomicActEditor from "./DialogTaxonomicActEditor";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import GavelIcon from "@mui/icons-material/Gavel";
import ScienceIcon from "@mui/icons-material/Science";
import NatureIcon from '@mui/icons-material/Nature';
import PublicIcon from "@mui/icons-material/Public";
import CategoryIcon from "@mui/icons-material/Category";
import SourceIcon from "@mui/icons-material/Source";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import SaveIcon from "@mui/icons-material/Save";
import DialogOccurrenceEditor from "./DialogOccurrenceEditor";

const TAG = "[DialogScientificNameEditor]";
const RanksNoLocalityConst = ["subgenus", "genus", "family", "order", "class", "phylum", "kingdom"];
const urlRegex = /^https?:\/\/[^\s]+$/i;

const formatPublicationCitation = (publication, page) => {
  if (!publication) return "";

  const authors = publication._authors || [];
  const year = publication.publication_date ? new Date(publication.publication_date).getFullYear() : '';

  let authorStr = '';
  if (authors.length === 1) {
      authorStr = authors[0]?.last_name || '';
  } else if (authors.length === 2) {
      authorStr = `${authors[0]?.last_name || ''} & ${authors[1]?.last_name || ''}`;
  } else if (authors.length > 2) {
      authorStr = `${authors[0]?.last_name || ''} et al.`;
  }

  if (!authorStr) {
    return publication.title_english || publication.title_original || '';
  }

  const pageStr = page ? `: p. ${page}` : '';
  return `${authorStr} (${year}${pageStr})`;
};

const AllowedRanks = [
  "kingdom",
  "phylum",
  "class",
  "order",
  "family",
  "genus",
  "subgenus",
  "species",
  "subspecies",
  "invalid",
];
const invalidTypeOptions = ["junior synonym", "senior synonym", "homonym", "nomen nudum", "emendation"];
const isSpeciesLikeRank = (r) => {
  if (!r) return false;
  const s = r.toLowerCase();
  return s.includes("species") || s === "sp." || s === "sp" || s === "subspecies";
};
const tabDefs = [
  { key: "core_taxonomy", label: "Core Taxonomy", icon: <AccountTreeIcon /> },
  { key: "nomenclatural_acts", label: "Nomenclatural Acts", icon: <HistoryEduIcon /> },
  { key: "occurrence", label: "Occurrence", icon: <TravelExploreIcon /> },
  { key: "ecology", label: "Ecology", icon: <NatureIcon /> },
  { key: "gbif", label: "GBIF", icon: <PublicIcon /> },
  { key: "other", label: "Other", icon: <MoreHorizIcon /> },
];

function generateId() {
  const guid =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  return `sci_name_${guid}`;
}

const toStr = (v) => (v == null ? "" : String(v));
const normalizeStringArray = (raw) => {
  const flattenVals = (val) => {
    if (val == null) return [];
    if (Array.isArray(val)) return val.flatMap((v) => flattenVals(v));

    if (typeof val === "string") {
      const trimmed = val.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return flattenVals(parsed);
      } catch {
        // not JSON array, continue
      }
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [trimmed];
    }

    return [String(val).trim()];
  };

  return flattenVals(raw).filter(Boolean);
};
function asUuidOrNull(v) {
  if (!v) return null;
  if (typeof v === "string") return /^[0-9a-f-]{36}$/i.test(v) ? v : null;
  if (typeof v === "object") {
    if (v.parent_uuid && /^[0-9a-f-]{36}$/i.test(v.parent_uuid)) return v.parent_uuid;
    if (v.uuid && /^[0-9a-f-]{36}$/i.test(v.uuid)) return v.uuid;
    if (v.id && /^[0-9a-f-]{36}$/i.test(v.id)) return v.id;
  }
  return null;
}
function asIdOrNull(v) {
  if (!v) return null;
  if (typeof v === "string") return v.trim() || null;
  if (typeof v === "object" && v.id != null) return String(v.id);
  return null;
}
const isInvalidRank = (rank) => typeof rank === "string" && rank.toLowerCase() === "invalid";
function buildTypeHostPayload(sel) {
  if (!sel) return null;
  return {
    taxon_id: sel.gbifId ? String(sel.gbifId) : null,
    scientific_name: sel.scientific_name || "",
    scientific_name_authorship: sel.scientific_name_authorship || "",
    taxon_rank: sel.taxon_rank || "",
    taxonomic_status: sel.taxonomic_status || "",
    nomenclatural_code: sel.nomenclatural_code || "",
    nomenclatural_status: sel.nomenclatural_status || "",
    kingdom: sel.kingdom || "",
    phylum: sel.phylum || "",
    class: sel.class || "",
    order: sel.order || "",
    superfamily: sel.superfamily || "",
    family: sel.family || "",
    subfamily: sel.subfamily || "",
    tribe: sel.tribe || "",
    subtribe: sel.subtribe || "",
    genus: sel.genus || "",
    subgenus: sel.subgenus || "",
    specific_epithet: sel.specific_epithet || "",
    infraspecific_epithet: sel.infraspecific_epithet || "",
    higher_classification: sel.higher_classification || "",
    taxon_remarks: sel.taxon_remarks || "",
    modified: new Date().toISOString(),
    source: sel.source_url || sel.source || (sel.source === "manual" ? "manual" : "GBIF"),
  };
}
const gbifUrl = (id) => (id ? `https://www.gbif.org/species/${id}` : null);

async function writeAudit({ action, id, before = null, after = null, actor }) {
  try {
    // audit_log 繝・・繝悶Ν縺ｮ螳溷惠繧ｫ繝ｩ繝縺ｫ蜷医ｏ縺帙※繧ｷ繝ｳ繝励Ν縺ｫ險倬鹸
    const payload = {
      table_name: "scientific_names",
      row_id: id,
      action,
      actor_id: actor?.id || null,
      created_at: new Date().toISOString(),
      before_data: before,
      after_data: after,
    };

    const { error } = await supabase.from("audit_log").insert([payload]);
    if (error) console.warn(`${TAG} audit log failed`, error);
  } catch (e) {
    console.warn(`${TAG} audit log skipped`, e);
  }
}

/* ---------- helpers ---------- */
async function detectExistingTable(tables, probeSelect = "id") {
  for (const t of tables) {
    const res = await supabase.from(t).select(probeSelect).limit(1);
    if (!res.error) return t;
  }
  return null;
}
const normalizeGbifHits = (arr = []) =>
  arr
    .filter(Boolean)
    .map((d) => ({
      key: d.key,
      scientificName: d.scientificName || d.canonicalName || "",
      canonicalName: d.canonicalName || "",
      authorship: d.authorship || "",
      rank: d.rank || "",
      family: d.family || "",
      genus: d.genus || "",
      status: d.taxonomicStatus || d.status || "",
      kingdom: d.kingdom || "",
      phylum: d.phylum || "",
      class: d.class || "",
      order: d.order || "",
      superfamily: d.superfamily || "",
      subfamily: d.subfamily || "",
      tribe: d.tribe || "",
      subtribe: d.subtribe || "",
      higherClassification: d.higherClassification || "",
    }))
    .filter((h) => h.key && h.scientificName);


function TabPanel({ current, value, children }) {
  if (current !== value) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function DialogScientificNameEditor({
  open = true,
  mode = "add",
  scientificName = null,
  onClose,
  localMode = false, // true のとき Supabase には書き込まず onSave に payload を返す
  onSave,
}) {
  // Internal state for edit mode, initialized from props
  const [isEdit, setIsEdit] = useState(mode === "edit");
  useEffect(() => {
    setIsEdit(mode === "edit");
  }, [mode]);
  const { user: authUser } = useSupabaseSession();
  const {
    ranks: ranksMaster,
    extantFossil: extantMaster,
    countries: countriesMaster,
    typeCategories: typeCategoriesMaster,
  } = useMasterData();

  // ---- form ----
  const [form, setForm] = useState({
    id: generateId(),
    name_spell_valid: "",
    name_spell_original: null,
    current_rank: null,
    current_invalid_type: null,
    current_invalid_homonym_with: null,
    original_rank: null,
    original_invalid_type: null,
    original_invalid_homonym_with: null,
    current_parent: null,
    original_parent: null,
    extant_fossil: null,
    remark: "",
    fossil_type: "",
    amber_group: "",
    geological_era: "",
    fossil_age_mya: "",
    taxonomic_note: "",
    gbif_usage_key: [],
    merged_gbif_ids: [],
    source_of_original_description: null,
    valid_name_id: null,
    type_taxa_id: null,
    type_locality: null,
    type_repository_id: null, // UUID
    page: "",
    type_category: "",
    zoobank_url: null,
    type_img_urls: null,
    type_remarks: "",
  });

  // ---- helpers ----
  const [imgRows, setImgRows] = useState([{ title: "", url: "" }]);
  const [typeTaxaRadio, setTypeTaxaRadio] = useState("");
  const [typeHostSelection, setTypeHostSelection] = useState(null);
  const [gbifResults, setGbifResults] = useState([]);
  const [gbifResultsOpen, setGbifResultsOpen] = useState(false);
  const [gbifSearchError, setGbifSearchError] = useState("");
  const [gbifSearching, setGbifSearching] = useState(false);
  const [gbifSearchText, setGbifSearchText] = useState("");
  const [ecologyTags, setEcologyTags] = useState([]);
  const [selectedEcologyTags, setSelectedEcologyTags] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const handleAuthorRemove = useCallback(
    (authorId) => setSelectedAuthors((prev) => prev.filter((a) => a.id !== authorId)),
    []
  );

  const [ranks, setRanks] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [publications, setPublications] = useState([]);
  const [countriesList, setCountriesList] = useState([]);
  const [typeCategories, setTypeCategories] = useState([]);
  const mastersLoadedRef = useRef(false);
  const lastLoadedId = useRef(null);
  const [activeTab, setActiveTab] = useState("core_taxonomy");

  const [loadingMaster, setLoadingMaster] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [taxonomicActs, setTaxonomicActs] = useState([]);
  const [actEditorOpen, setActEditorOpen] = useState(false);
  const [targetAct, setTargetAct] = useState(null);

  const [editTaxonomyOpen, setEditTaxonomyOpen] = useState(false);
  const [editNameDetailsOpen, setEditNameDetailsOpen] = useState(false);
  const [editTypeInfoOpen, setEditTypeInfoOpen] = useState(false);
  const [editSourceOpen, setEditSourceOpen] = useState(false);
  const [editExtantFossilOpen, setEditExtantFossilOpen] = useState(false);
  const [editGbifOpen, setEditGbifOpen] = useState(false);
  const [editOtherOpen, setEditOtherOpen] = useState(false);

  const [occurrences, setOccurrences] = useState([]);
  const [occurrenceEditorOpen, setOccurrenceEditorOpen] = useState(false);
  const [targetOccurrence, setTargetOccurrence] = useState(null);

  const { scientificNames, refresh } = useFetchScientificNames();
  const safeScientificNameData = useMemo(() => scientificNames || [], [scientificNames]);
  const lookupSciLabel = useCallback(
    (id) => {
      if (!id) return "";
      const hit = safeScientificNameData.find((s) => s.id === id);
      return hit?.name_spell_valid || hit?.name_spell_original || id;
    },
    [safeScientificNameData]
  );
  const resetState = useCallback(() => {
    lastLoadedId.current = null;
    setForm({
      id: generateId(),
      name_spell_valid: "",
      name_spell_original: null,
      current_rank: null,
      current_invalid_type: null,
      current_invalid_homonym_with: null,
      original_rank: null,
      original_invalid_type: null,
      original_invalid_homonym_with: null,
      current_parent: null,
      original_parent: null,
      extant_fossil: null,
      remark: "",
      fossil_type: "",
      amber_group: "",
      geological_era: "",
      fossil_age_mya: "",
      taxonomic_note: "",
      authority_year: "",
      gbif_usage_key: [],
      merged_gbif_ids: [],
      type_sex: "",
      source_of_original_description: null,
      valid_name_id: null,
      type_taxa_id: null,
      type_locality: null,
      type_repository_id: null,
      page: "",
      type_category: "",
      zoobank_url: null,
      type_img_urls: null,
      type_remarks: "",
    });
    setImgRows([{ title: "", url: "" }]);
    setTypeTaxaRadio("");
    setTypeHostSelection(null);
    setSelectedEcologyTags([]);
    setGbifResults([]);
    setGbifResultsOpen(false);
    setGbifSearchError("");
    setGbifSearching(false);
    setGbifSearchText("");
    setSelectedAuthors([]);
    setRanks([]);
    setTaxonomicActs([]);
    setOccurrences([]);
    setStatuses([]);
    setPublications([]);
    setCountriesList([]);
    setTypeCategories([]);
    setLoadingMaster(!mastersLoadedRef.current);
    setSaving(false);
    setActiveTab("core_taxonomy");
    setEditTaxonomyOpen(false);
    setEditNameDetailsOpen(false);
    setEditTypeInfoOpen(false);
    setEditSourceOpen(false);
    setEditExtantFossilOpen(false);
    setEditGbifOpen(false);
    setEditOtherOpen(false);
  }, []);

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const pastYears = Array.from({ length: 150 }, (_, i) => `${currentYear - i}`);
    const futureYears = Array.from({ length: 10 }, (_, i) => `${currentYear + 1 + i}`);
    return [...futureYears.reverse(), ...pastYears];
  }, []);

  // 遞ｮ繝ｻ莠懃ｨｮ縺ｪ縺ｩ縺ｮ蝣ｴ蜷医・縺ｿ Type Specimen 繧ｻ繧ｯ繧ｷ繝ｧ繝ｳ繧定｡ｨ遉ｺ
  const showTypeSpecimen = useMemo(() => {
    return isSpeciesLikeRank(form.current_rank) || isSpeciesLikeRank(form.original_rank);
  }, [form.current_rank, form.original_rank]);

  const gbifUsageKeysValue = useMemo(
    () => normalizeStringArray(form.gbif_usage_key),
    [form.gbif_usage_key]
  );
  const mergedGbifIdsValue = useMemo(
    () => normalizeStringArray(form.merged_gbif_ids),
    [form.merged_gbif_ids]
  );

  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);
  const handleGbifUsageKeyChange = useCallback(
    (value) => handleChange("gbif_usage_key", normalizeStringArray(value)),
    [handleChange]
  );
  const handleMergedGbifIdsChange = useCallback(
    (value) => handleChange("merged_gbif_ids", normalizeStringArray(value)),
    [handleChange]
  );

  // Clear invalid-related fields when rank leaves "invalid"
  useEffect(() => {
    if (!isInvalidRank(form.current_rank) && (form.current_invalid_type || form.current_invalid_homonym_with)) {
      setForm((prev) => ({
        ...prev,
        current_invalid_type: null,
        current_invalid_homonym_with: null,
      }));
    }
  }, [form.current_rank, form.current_invalid_homonym_with, form.current_invalid_type]);

  useEffect(() => {
    if (!isInvalidRank(form.original_rank) && (form.original_invalid_type || form.original_invalid_homonym_with)) {
      setForm((prev) => ({
        ...prev,
        original_invalid_type: null,
        original_invalid_homonym_with: null,
      }));
    }
  }, [form.original_rank, form.original_invalid_homonym_with, form.original_invalid_type]);

  const handleTabChange = useCallback((_, v) => setActiveTab(v), []);
  const computedTabs = useMemo(() => {
    if (!isEdit) {
      return tabDefs.filter((t) => t.key === "core_taxonomy");
    }
    return tabDefs;
  }, [isEdit]);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open, resetState]);

  useEffect(() => {
    const base = form.name_spell_valid?.trim() || "";
    let suggested = base;
    if (isSpeciesLikeRank(form.current_rank)) {
      const parentLabel = form.current_parent ? lookupSciLabel(form.current_parent) : "";
      if (parentLabel) suggested = `${parentLabel} ${base}`.trim();
    }
    if (!gbifSearchText || gbifSearchText === base) {
      setGbifSearchText(suggested);
    }
  }, [form.name_spell_valid, form.current_rank, form.current_parent, lookupSciLabel, gbifSearchText]);

  useEffect(() => {
    if (!computedTabs.find((t) => t.key === activeTab)) {
      setActiveTab(computedTabs[0]?.key || "core_taxonomy");
    }
  }, [computedTabs, activeTab]);

  // ---------- edit init ----------
  useEffect(() => {
    if (!scientificName) return;
    if (lastLoadedId.current === scientificName.id) return;
    lastLoadedId.current = scientificName.id;

    console.log("[DialogScientificNameEditor] scientificName prop received:", scientificName);
    setForm((prev) => ({
      ...prev,
      id: scientificName.id ?? generateId(),
      name_spell_valid: scientificName.name_spell_valid ?? "",
      name_spell_original: scientificName.name_spell_original ?? null,
      current_rank: scientificName.current_rank ?? null,
      current_invalid_type: scientificName.current_invalid_type ?? null,
      current_invalid_homonym_with: scientificName.current_invalid_homonym_with ?? null,
      original_rank: scientificName.original_rank ?? null,
      original_invalid_type: scientificName.original_invalid_type ?? null,
      original_invalid_homonym_with: scientificName.original_invalid_homonym_with ?? null,
      current_parent: scientificName.current_parent ?? null,
      original_parent: scientificName.original_parent ?? null,
      extant_fossil: scientificName.extant_fossil ?? null,
      fossil_type: scientificName.fossil_type ?? "",
      amber_group: scientificName.amber_group ?? "",
      geological_era: scientificName.geological_era ?? "",
      fossil_age_mya: scientificName.fossil_age_mya ?? "",
      remark: scientificName.remark ?? "",
      taxonomic_note: scientificName.taxonomic_note ?? "",
      authority_year: toStr(scientificName.authority_year),
      merged_gbif_ids: normalizeStringArray(scientificName.merged_gbif_ids),
      gbif_usage_key: normalizeStringArray(scientificName.gbif_usage_key),
      // GBIF 検索テキストは既存名を初期値に
      gbif_search_text: undefined,
      type_sex: scientificName.type_sex ?? "",
      source_of_original_description: scientificName.source_of_original_description ?? null,
      valid_name_id: scientificName.valid_name_id ?? null,
      type_taxa_id: scientificName.type_taxa_id ?? null,
      type_locality: scientificName.type_locality ?? null,
      type_repository_id: scientificName.type_repository_id ?? null,
      page: scientificName.page ?? "",
      type_category: scientificName.type_category ?? "",
      zoobank_url: scientificName.zoobank_url ?? null,
      type_img_urls: scientificName.type_img_urls ?? null,
      type_remarks: scientificName.type_remarks ?? "",
    }));

    (async () => {
      try {
        const { data: rel } = await supabase
          .from("scientific_name_and_author")
          .select("author_order, researcher_id")
          .eq("scientific_name_id", scientificName.id)
          .order("author_order");

        const ids = (rel || []).map((r) => r.researcher_id);
        if (!ids?.length) {
          setSelectedAuthors([]);
          return;
        }

        const { data: researchers } = await supabase.from("researchers").select("*").in("id", ids);
        const byId = new Map((researchers || []).map((r) => [r.id, r]));
        const sorted = (rel || [])
          .sort((a, b) => a.author_order - b.author_order)
          .map((r) => byId.get(r.researcher_id))
          .filter(Boolean);
      setSelectedAuthors(sorted);
      } catch (e) {
        console.warn(`${TAG} load authors`, e);
      }
    })();

    (async () => {
      try {
        const { data } = await supabase
          .from("type_host_rel")
          .select("type_host(*), note")
          .eq("scientific_name_id", scientificName.id)
          .maybeSingle();
        const host = data?.type_host;
        if (host) {
          setTypeHostSelection({
            source: host.taxon_id ? "gbif" : "manual",
            gbifId: host.taxon_id || null,
            scientific_name: host.scientific_name || "",
            scientific_name_authorship: host.scientific_name_authorship || "",
            taxon_rank: host.taxon_rank || "",
            taxonomic_status: host.taxonomic_status || "",
            nomenclatural_code: host.nomenclatural_code || "",
            nomenclatural_status: host.nomenclatural_status || "",
            kingdom: host.kingdom || "",
            phylum: host.phylum || "",
            class: host.class || "",
            order: host.order || "",
            superfamily: host.superfamily || "",
            family: host.family || "",
            subfamily: host.subfamily || "",
            tribe: host.tribe || "",
            subtribe: host.subtribe || "",
            genus: host.genus || "",
            subgenus: host.subgenus || "",
            specific_epithet: host.specific_epithet || "",
            infraspecific_epithet: host.infraspecific_epithet || "",
            higher_classification: host.higher_classification || "",
            taxon_remarks: host.taxon_remarks || "",
            source_url: host.taxon_id ? gbifUrl(host.taxon_id) : "",
            note: data?.note || null,
          });
        } else {
          setTypeHostSelection(null);
        }
      } catch (e) {
        console.warn(`${TAG} load type_host_rel`, e);
      }
    })();

    (async () => {
      try {
        const { data } = await supabase
          .from("scientific_name_ecological_tags")
          .select("tag_id")
          .eq("scientific_name_id", scientificName.id);
        const ids = (data || []).map((d) => d.tag_id).filter(Boolean);
        setSelectedEcologyTags(ids);
      } catch (e) {
        console.warn("[ecology tags] load relations failed", e);
      }
    })();

    setGbifSearchText(scientificName.name_spell_valid || "");
  }, [isEdit, scientificName]);

  // ---------- Taxonomic Acts ----------
  const fetchActs = useCallback(async () => {
    if (!form.id) return;

    // Workaround: Fetch act types separately to avoid PostgREST join error due to missing FK
    const { data: actTypes, error: actTypesError } = await supabase
      .from("taxonomic_act_types")
      .select("id, name");
    
    if (actTypesError) {
      console.error(`${TAG} fetch act types error`, actTypesError);
    }
    const actTypeMap = new Map((actTypes || []).map(t => [t.id, t.name]));

    const { data, error } = await supabase
      .from("taxonomic_acts")
      .select(`
        *,
        scientific_name:scientific_name_id ( id, name_spell_valid ),
        related_name:related_name_id ( name_spell_valid ),
        publication:publication_id_uuid ( id, title_english, title_original, publication_date )
      `)
      .or(`scientific_name_id.eq."${form.id}",related_name_id.eq."${form.id}"`);

    if (error) {
      console.error(`${TAG} fetchActs error`, error);
      setTaxonomicActs([]); // Clear on error
      return;
    }

    if (!data || data.length === 0) {
      setTaxonomicActs([]);
      return;
    }

    // Get all publication text IDs
    const pubTextIds = data.map(act => act.publication?.id).filter(Boolean);

    // Fetch authors for these publications
    let authorsByPubId = new Map();
    if (pubTextIds.length > 0) {
      const { data: authorLinks, error: authorError } = await supabase
          .from('publications_authors')
          .select('publication_id, author_order, researcher:researchers(id, last_name)')
          .in('publication_id', pubTextIds)
          .order('author_order');
      
      if (authorError) {
          console.error(`${TAG} fetch author links error`, authorError);
      }

      if (authorLinks) {
          for (const link of authorLinks) {
              if (!authorsByPubId.has(link.publication_id)) {
                  authorsByPubId.set(link.publication_id, []);
              }
              if (link.researcher) {
                authorsByPubId.get(link.publication_id).push(link.researcher);
              }
          }
      }
    }

    // Manually join the act type name and publication authors
    const actsWithData = data.map(act => {
      const pubAuthors = act.publication?.id ? authorsByPubId.get(act.publication.id) : [];
      return {
        ...act,
        act_type: { name: actTypeMap.get(parseInt(act.act_type_id, 10)) || `ID: ${act.act_type_id}` },
        publication: act.publication ? { ...act.publication, _authors: pubAuthors || [] } : null
      };
    });
    setTaxonomicActs(actsWithData);
  }, [form.id]);

  useEffect(() => {
    if (open && isEdit) {
      fetchActs();
    } else {
      setTaxonomicActs([]);
    }
  }, [open, isEdit, fetchActs]);

  const handleAddAct = () => {
    setTargetAct(null);
    setActEditorOpen(true);
  };
  const handleEditAct = (act) => {
    setTargetAct(act);
    setActEditorOpen(true);
  };
  const handleDeleteAct = async (id) => {
    if (!window.confirm("Delete this act?")) return;
    await supabase.from("taxonomic_acts").delete().eq("id", id);
    fetchActs();
  };
  const handleActSaved = () => {
    fetchActs();
  };

  // ---------- Occurrences ----------
  const fetchOccurrences = useCallback(async () => {
    if (!form.id) return;
    const { data, error } = await supabase
      .from("occurrences")
      .select(`
        *,
        publication:publication_id_uuid ( id, title_english, title_original, publication_date )
      `)
      .eq("scientific_name_id", form.id);
    if (error) {
      console.error(`${TAG} fetchOccurrences error`, error);
      setOccurrences([]);
      return;
    }

    const distsWithAuthors = await Promise.all((data || []).map(async (dist) => {
      if (!dist.publication?.id) return dist;
      const { data: authorLinks } = await supabase
        .from('publications_authors')
        .select('author_order, researcher:researchers(id, last_name)')
        .eq('publication_id', dist.publication.id)
        .order('author_order');
      return { ...dist, publication: { ...dist.publication, _authors: (authorLinks || []).map(l => l.researcher) } };
    }));

    setOccurrences(distsWithAuthors);
  }, [form.id]);

  useEffect(() => {
    if (open && isEdit) {
      fetchOccurrences();
    } else {
      setOccurrences([]);
    }
  }, [open, isEdit, fetchOccurrences]);

  const handleAddOccurrence = () => { setTargetOccurrence(null); setOccurrenceEditorOpen(true); };
  const handleEditOccurrence = (occ) => { setTargetOccurrence(occ); setOccurrenceEditorOpen(true); };
  const handleDeleteOccurrence = async (id) => {
    if (!window.confirm("Delete this distribution record?")) return;
    await supabase.from("occurrences").delete().eq("id", id);
    fetchOccurrences();
  };
  const handleOccurrenceSaved = () => fetchOccurrences();

  // ---------- publications: flat + batched manual join (400蝗樣∩) ----------
  async function fetchPublicationsSmart() {
    // 1) publications 縺ｯ譛蟆丞・縺ｮ縺ｿ
    const { data: pubs, error: pErr } = await supabase
      .from("publications")
      .select("id, id_uuid, title_english, publication_date, volume, number, page, journal_id")
      .order("id");
    if (pErr || !pubs) {
      console.debug("[pubs] flat select failed", pErr);
      return [];
    }

    // 2) Journal 蜷搾ｼ壹ユ繝ｼ繝悶Ν蜷阪ｒ閾ｪ蜍墓､懷・ 竊・batched 蜿門ｾ暦ｼ亥・縺ｯ * 縺ｧ蜿悶ｊ縲√ヵ繧ｩ繝ｼ繝ｫ繝舌ャ繧ｯ蜷阪ｒ驕ｸ縺ｶ・・
    const jids = Array.from(new Set(pubs.map((p) => p.journal_id).filter(Boolean)));
    let journalMap = new Map();
    if (jids.length) {
      const journalTable = await detectExistingTable(["journals", "journal"], "id");
      if (journalTable) {
        try {
          const journals = await selectInBatched({
            table: journalTable,
            column: "id",
            values: jids,
            select: "*", // 竊・name_english 遲峨′辟｡縺ДB縺ｧ繧り誠縺｡縺ｪ縺・ｈ縺・↓
            chunkSize: 500,
          });
          const pickName = (j) =>
            j?.name_english ??
            j?.name_original ??
            j?.name ??
            j?.title_english ??
            j?.title ??
            j?.slug ??
            "";
          (journals || []).forEach((j) => journalMap.set(j.id, pickName(j)));
        } catch (e) {
          console.warn("[fetchPublicationsSmart] journal fetch error", e);
        }
      }
    }

    // 3) 闡苓・Μ繝ｳ繧ｯ・壻ｸｭ髢薙ユ繝ｼ繝悶Ν蜷阪ｒ閾ｪ蜍墓､懷・・・atched・・
    const pubIds = pubs.map((p) => p.id);
    let links = [];
    if (pubIds.length) {
      const linkTable = await detectExistingTable(
        ["publications_authors", "publication_authors"],
        "publication_id"
      );
      if (linkTable) {
        // 縺ｾ縺・researcher_id 縺ｧ隧ｦ縺励√ム繝｡縺ｪ繧・author_id 縺ｫ繝輔か繝ｼ繝ｫ繝舌ャ繧ｯ
        try {
          links = await selectInBatched({
            table: linkTable,
            column: "publication_id",
            values: pubIds,
            select: "publication_id, author_order, researcher_id",
            chunkSize: 500,
            order: { column: "author_order", ascending: true },
          });
        } catch (e1) {
          try {
            links = await selectInBatched({
              table: linkTable,
              column: "publication_id",
              values: pubIds,
              select: "publication_id, author_order, author_id",
              chunkSize: 500,
              order: { column: "author_order", ascending: true },
            });
          } catch (e2) {
            console.warn("[fetchPublicationsSmart] link fetch error", e2);
            links = [];
          }
        }
      }
    }

    // 4) 豌丞錐縺ｮ隗｣豎ｺ・・esearchers 蜆ｪ蜈・竊・authors・会ｼ・atched・・
    const researcherIds = Array.from(new Set(links.map((r) => r.researcher_id).filter(Boolean)));
    const authorIds = Array.from(new Set(links.map((r) => r.author_id).filter(Boolean)));
    const lastNameMap = new Map();

    if (researcherIds.length) {
      try {
        const researchers = await selectInBatched({
          table: "researchers",
          column: "id",
          values: researcherIds,
          select: "id, last_name",
          chunkSize: 500,
        });
        (researchers || []).forEach((r) =>
          lastNameMap.set(`r:${r.id}`, r.last_name || "")
        );
      } catch (e) {
        console.warn("[fetchPublicationsSmart] researchers fetch error", e);
      }
    }
    if (!researcherIds.length && authorIds.length) {
      try {
        const authors = await selectInBatched({
          table: "authors",
          column: "id",
          values: authorIds,
          select: "id, last_name",
          chunkSize: 500,
        });
        (authors || []).forEach((a) =>
          lastNameMap.set(`a:${a.id}`, a.last_name || "")
        );
      } catch (e) {
        console.warn("[fetchPublicationsSmart] authors fetch error", e);
      }
    }

    // 5) publication_id 竊・闡苓・・蛻励・繝槭ャ繝暦ｼ・uthor_order鬆・ｼ・
    const linksMap = links.reduce((m, row) => {
      const arr = m.get(row.publication_id) || [];
      const last =
        (row.researcher_id && lastNameMap.get(`r:${row.researcher_id}`)) ||
        (row.author_id && lastNameMap.get(`a:${row.author_id}`)) ||
        null;
      arr.push({ author_order: row.author_order, last_name_eng: last });
      return m.set(row.publication_id, arr);
    }, new Map());

    // 6) 霑泌唆・嗚ournal_name 縺ｨ _authorsRel 繧剃ｻ倅ｸ・
    return pubs.map((p) => ({
      ...p,
      journal_name: journalMap.get(p.journal_id) || "",
      _authorsRel: (linksMap.get(p.id) || []).sort(
        (a, b) => (a?.author_order || 0) - (b?.author_order || 0)
      ),
    }));
  }

  // ---------- masters ----------
  useEffect(() => {
    if (!open || mastersLoadedRef.current) return;
    let mounted = true;
    setLoadingMaster(true);

    (async () => {
      try {
        // Relationship 用の学名 ID
        // マスタ群は Context 優先
        if (mounted) {
          if (ranksMaster?.length) setRanks(ranksMaster.map((d) => d.id || d));
          if (extantMaster?.length) setStatuses(extantMaster.map((d) => d.id || d));
          if (countriesMaster?.length) setCountriesList(countriesMaster);
          if (typeCategoriesMaster?.length) setTypeCategories(typeCategoriesMaster);
        }

        // Publications を賢く取得
        const pubs = await fetchPublicationsSmart();
        if (mounted) {
          setPublications(pubs);
          mastersLoadedRef.current = true;
        }
      } finally {
        if (mounted) setLoadingMaster(false);
      }
    })();

    // 生態タグのマスタ取得
    (async () => {
      try {
        const { data, error } = await supabase.from("ecological_tags").select("*").order("name");
        if (!error && mounted) setEcologyTags(data || []);
      } catch (e) {
        console.warn("[ecology tags] fetch failed", e);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [open, ranksMaster, extantMaster, countriesMaster, typeCategoriesMaster]);

  // ---------- helpers for tabs ----------
  const editingSummary = useMemo(() => {
    const actionLabel = isEdit ? "Editing" : "Adding";
    const parentLabel = form.current_parent ? lookupSciLabel(form.current_parent) : "no parent yet";
    const parentMissing = !form.current_parent;
    const nameLabel = form.name_spell_valid?.trim() || "(name not set)";
    const nameMissing = !form.name_spell_valid?.trim();
    const authorsLabel = selectedAuthors.length
      ? selectedAuthors
          .map((a) => a?.last_name || a?.family_name || a?.full_name || a?.first_name || a?.name || a?.id)
          .filter(Boolean)
          .join(", ")
      : "author not set";
    const authorsMissing = selectedAuthors.length === 0;
    const yearLabel = toStr(form.authority_year).trim() || "year not set";
    const yearMissing = !toStr(form.authority_year).trim();
    return {
      actionLabel,
      parentLabel,
      parentMissing,
      nameLabel,
      nameMissing,
      authorsLabel,
      authorsMissing,
      yearLabel,
      yearMissing,
    };
  }, [form.authority_year, form.current_parent, form.name_spell_valid, isEdit, lookupSciLabel, selectedAuthors]);

  const imgRowsInvalid = useMemo(
    () =>
      imgRows.some((r) => {
        const titleFilled = r.title?.trim() !== "";
        const urlFilled = r.url?.trim() !== "";
        const urlOk = !urlFilled || urlRegex.test(r.url);
        return titleFilled !== urlFilled || !urlOk;
      }),
    [imgRows]
  );
  const renderPublicationLabel = useCallback((pub) => {
    if (!pub) return "";
    const title = pub.title_english || pub.title_original || pub.title || "";
    const authors = (pub._authorsRel || []).map((a) => a.last_name_eng).filter(Boolean).join(", ");
    return authors ? `${title} / ${authors}` : title;
  }, []);
  const setImg = useCallback((idx, key, value) => {
    setImgRows((prev) => prev.map((row, i) => (i === idx ? { ...row, [key]: value } : row)));
  }, []);
  const addImgRow = useCallback(() => {
    setImgRows((prev) => [...prev, { title: "", url: "" }]);
  }, []);
  const removeImgRow = useCallback((idx) => {
    setImgRows((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleTypeTaxaRadio = useCallback(
    (value) => {
      setTypeTaxaRadio(value);
      if (value === "own") {
        const idVal = form.id?.trim() || null;
        handleChange("type_taxa_id", idVal);
      } else if (value === "none") {
        handleChange("type_taxa_id", null);
      }
    },
    [form.id, handleChange]
  );

  const errors = useMemo(
    () => ({
      id: !form.id?.trim(),
      nameSpell: !form.name_spell_valid?.trim(),
      currentRank: !form.current_rank,
      current_parent: !form.current_parent,
      typeHost: false,
      currentInvalidType: isInvalidRank(form.current_rank) && !form.current_invalid_type,
      currentInvalidHomonym:
        isInvalidRank(form.current_rank) &&
        form.current_invalid_type === "homonym" &&
        !(form.current_invalid_homonym_with && String(form.current_invalid_homonym_with).trim()),
      originalInvalidType:
        isInvalidRank(form.original_rank) && form.original_rank && !form.original_invalid_type,
      originalInvalidHomonym:
        isInvalidRank(form.original_rank) &&
        form.original_invalid_type === "homonym" &&
        !(form.original_invalid_homonym_with && String(form.original_invalid_homonym_with).trim()),
      authority: selectedAuthors.length === 0,
      authorityYear: (() => {
        const y = toStr(form.authority_year).trim();
        return !y || !/^\d{4}$/.test(y);
      })(),
      typeImg: imgRowsInvalid,
      typeTaxaId: typeTaxaRadio === "other" && !form.type_taxa_id,
      zoobankUrl: !!form.zoobank_url && !/^https?:\/\/(www\.)?zoobank\.org\/.+$/i.test(form.zoobank_url),
      gbifUsageKeys: gbifUsageKeysValue.some((key) => isNaN(Number(key))),
    }),
    [form, selectedAuthors, typeTaxaRadio, imgRowsInvalid, gbifUsageKeysValue]
  );

  const handleGbifSearch = useCallback(async () => {
    const baseName = (gbifSearchText || form.name_spell_valid || "").trim();
    let q = baseName;
    if (isSpeciesLikeRank(form.current_rank)) {
      const parentLabel = form.current_parent ? lookupSciLabel(form.current_parent) : "";
      const alreadyHasParent =
        parentLabel && baseName.toLowerCase().startsWith(parentLabel.toLowerCase());
      if (parentLabel && !alreadyHasParent) {
        const combined = `${parentLabel} ${baseName}`.trim();
        q = combined;
        console.log("[GBIF search] using parent + name", { parentLabel, baseName, combined });
      }
    }
    if (!q || q.length < 3) {
      setGbifSearchError("3文字以上の学名を入力してください。");
      return;
    }
    setGbifSearching(true);
    setGbifSearchError("");
    console.log("[GBIF search] query start", { q, rank: form.current_rank, parent: form.current_parent });
    try {
      const endpoints = [
        `https://api.gbif.org/v1/species/suggest?q=${encodeURIComponent(q)}&limit=20`,
        `https://api.gbif.org/v1/species/search?q=${encodeURIComponent(q)}&limit=20`,
      ];
      let hits = [];
      for (const url of endpoints) {
        const res = await fetch(url);
        console.log("[GBIF search] fetched", { url, status: res.status });
        if (!res.ok) continue;
        const data = await res.json();
        const norm = normalizeGbifHits(Array.isArray(data?.results) ? data.results : data);
        console.log("[GBIF search] normalized hits", { url, count: norm.length });
        hits = hits.concat(norm);
        if (hits.length) break; // suggest で取れれば search は不要
      }
      // 重複除去
      const seen = new Set();
      const uniq = hits.filter((h) => {
        if (seen.has(h.key)) return false;
        seen.add(h.key);
        return true;
      });
      setGbifResults(uniq);
      setGbifResultsOpen(uniq.length > 0);
      if (uniq.length === 0) setGbifSearchError("該当なし");
      console.log("[GBIF search] final hits", { count: uniq.length, ids: uniq.map((h) => h.key) });
    } catch (e) {
      console.log("[GBIF search] error", e);
      setGbifSearchError(e.message || "Search failed");
      setGbifResults([]);
      setGbifResultsOpen(false);
    } finally {
      setGbifSearching(false);
    }
  }, [gbifSearchText, form.current_rank, form.current_parent, lookupSciLabel, form.name_spell_valid]);

  const upsertTypeHostRel = useCallback(
    async (sciId) => {
      if (!sciId) return null;
      if (!typeHostSelection) {
        await supabase.from("type_host_rel").delete().eq("scientific_name_id", sciId);
        return null;
      }
      const payload = buildTypeHostPayload(typeHostSelection);
      if (!payload?.scientific_name) throw new Error("Type host payload is empty.");

      let hostId = null;
      if (payload.taxon_id) {
        const { data: existing } = await supabase
          .from("type_host")
          .select("id")
          .eq("taxon_id", payload.taxon_id)
          .maybeSingle();
        if (existing?.id) hostId = existing.id;
      }

      if (!hostId) {
        const { data: inserted, error: hostErr } = await supabase
          .from("type_host")
          .insert([payload])
          .select("id")
          .maybeSingle();
        if (hostErr) throw hostErr;
        hostId = inserted?.id;
      }

      if (!hostId) throw new Error("Failed to resolve type_host id.");

      const rel = {
        scientific_name_id: sciId,
        type_host_id: hostId,
        note: typeHostSelection.note || null,
      };
      const { error: relErr } = await supabase
        .from("type_host_rel")
        .upsert(rel, { onConflict: "scientific_name_id" });
      if (relErr) throw relErr;
      return hostId;
    },
    [typeHostSelection]
  );

  const syncEcologyTags = useCallback(
    async (sciId) => {
      if (!sciId) return;
      try {
        await supabase.from("scientific_name_ecological_tags").delete().eq("scientific_name_id", sciId);
        if (selectedEcologyTags.length > 0) {
          const rows = selectedEcologyTags.map((tagId) => ({
            scientific_name_id: sciId,
            tag_id: tagId,
          }));
          await supabase.from("scientific_name_ecological_tags").insert(rows);
        }
      } catch (e) {
        console.warn("[ecology tags] sync failed", e);
      }
    },
    [selectedEcologyTags]
  );

  const tabErrorMap = useMemo(
    () => ({
      core_taxonomy:
        errors.currentRank ||
        errors.current_parent ||
        errors.currentInvalidType ||
        errors.currentInvalidHomonym ||
        errors.originalInvalidType ||
        errors.originalInvalidHomonym ||
        errors.nameSpell ||
        errors.authority ||
        errors.authorityYear ||
        errors.typeImg ||
        errors.typeTaxaId ||
        errors.zoobankUrl,
      nomenclatural_acts: false,
      occurrence: false,
      gbif: errors.gbifUsageKeys || false,
      ecology: errors.typeHost || false,
      other: false,
    }),
    [errors]
  );

  // ---------- submit ----------
  const handleSubmit = useCallback(async (closeOnSuccess = true) => {
    if (saving) return; // 二重送信を防止するガード節

    if (Object.values(errors).some(Boolean)) {
      alert("Please fix the errors indicated in the form before submitting.");
      return;
    }

    const shouldClose = typeof closeOnSuccess === 'boolean' ? closeOnSuccess : true;
    setSaving(true);
    const trimmedId = form.id.trim();

    const imgObj = imgRows.reduce((acc, { title, url }) => {
      const t = title.trim();
      const u = url.trim();
      if (t && u && urlRegex.test(u)) acc[t] = u;
      return acc;
    }, {});
    const finalTypeImgUrls = Object.keys(imgObj).length > 0 ? imgObj : null;

    let finalTypeLocality = form.type_locality;
    if (form.current_rank && RanksNoLocalityConst.includes(form.current_rank.toLowerCase())) {
      finalTypeLocality = null;
    } else if (finalTypeLocality === "") {
      finalTypeLocality = null;
    }

    const repoUuid = asUuidOrNull(form.type_repository_id);
    const sourcePubId = asIdOrNull(form.source_of_original_description);
    const currentHomonymId = form.current_invalid_homonym_with
      ? String(form.current_invalid_homonym_with).trim()
      : null;
    const originalHomonymId = form.original_invalid_homonym_with
      ? String(form.original_invalid_homonym_with).trim()
      : null;

    const basePayload = {
      name_spell_valid: form.name_spell_valid.trim(),
      name_spell_original: form.name_spell_original?.trim() || null,
      current_rank: form.current_rank,
      current_invalid_type: form.current_invalid_type || null,
      current_invalid_homonym_with: currentHomonymId,
      original_rank: form.original_rank || null,
      original_invalid_type: form.original_invalid_type || null,
      original_invalid_homonym_with: originalHomonymId,
      extant_fossil: form.extant_fossil || null,
      fossil_type: form.fossil_type || null,
      amber_group: form.amber_group || null,
      geological_era: form.geological_era || null,
      fossil_age_mya: form.fossil_age_mya || null,
      remark: form.remark?.trim() || null,
      taxonomic_note: form.taxonomic_note?.trim() || null,
      authority_year: toStr(form.authority_year).trim(),
      gbif_id:
        gbifUsageKeysValue?.length > 0
          ? String(gbifUsageKeysValue[0]).trim()
          : mergedGbifIdsValue?.length > 0
            ? String(mergedGbifIdsValue[0]).trim()
            : null,
      gbif_usage_key: gbifUsageKeysValue?.length > 0 ? gbifUsageKeysValue : null,
      merged_gbif_ids: mergedGbifIdsValue?.length > 0 ? mergedGbifIdsValue : null,
      type_sex: form.type_sex || null,
      type_locality: finalTypeLocality,
      type_repository_id: repoUuid,
      page: form.page?.trim() || null,
      type_category: form.type_category || null,
      zoobank_url: form.zoobank_url?.trim() || null,
      type_img_urls: finalTypeImgUrls,
      type_remarks: form.type_remarks?.trim() || null,
      last_update: new Date().toISOString(),
    };

    try {
      if (!isEdit) {
        const { data: dup, error: checkError } = await supabase
          .from("scientific_names")
          .select("id")
          .eq("id", trimmedId)
          .maybeSingle();
        if (checkError) throw new Error(`Failed to check for duplicate ID: ${checkError.message}`);
        if (dup) throw new Error(`Duplicate ID found: ${trimmedId}. Please retry.`);

        const partialPayload = { id: trimmedId, ...basePayload };
        const { error: insertError } = await supabase.from("scientific_names").insert([partialPayload]);
        if (insertError) throw new Error(`Insert failed: ${insertError.message}`);

        const updatePayload = {
          current_parent: form.current_parent || null,
          original_parent: form.original_parent || null,
          valid_name_id: null,
          type_taxa_id: (typeTaxaRadio === "own" ? trimmedId : form.type_taxa_id) || null,
          source_of_original_description: sourcePubId,
          type_remarks: basePayload.type_remarks,
          last_update: new Date().toISOString(),
        };
        const { error: upErr } = await supabase.from("scientific_names").update(updatePayload).eq("id", trimmedId);
        if (upErr) throw new Error(`Update after insert failed: ${upErr.message}`);

        if (selectedAuthors.length > 0) {
          const authorLinks = selectedAuthors.map((author, index) => ({
            scientific_name_id: trimmedId,
            researcher_id: author.id,
            author_order: index + 1,
          }));
          await supabase.from("scientific_name_and_author").insert(authorLinks);
        }

        await upsertTypeHostRel(trimmedId);
        await syncEcologyTags(trimmedId);

        await writeAudit({
          action: "insert",
          id: trimmedId,
          before: null,
          after: { id: trimmedId, ...basePayload, ...updatePayload },
          actor: authUser
            ? { id: authUser.id, display: authUser.user_metadata?.display_name || authUser.email || null }
            : null,
        });
        setIsEdit(true);
      } else {
        const idForEdit = scientificName?.id;
        if (!idForEdit) throw new Error("Invalid edit target: id is missing.");

        const { data: beforeRow } = await supabase
          .from("scientific_names")
          .select("*")
          .eq("id", form.id)
          .maybeSingle();

        const updatePayload = {
          ...basePayload,
          current_parent: form.current_parent || null,
          original_parent: form.original_parent || null,
          valid_name_id: null,
          type_taxa_id: form.type_taxa_id || null,
          source_of_original_description: sourcePubId,
          type_remarks: basePayload.type_remarks,
        };
        const { error: updateErr } = await supabase.from("scientific_names").update(updatePayload).eq("id", form.id);
        if (updateErr) throw new Error(`Update failed: ${updateErr.message}`);

        await supabase.from("scientific_name_and_author").delete().eq("scientific_name_id", form.id);
        if (selectedAuthors.length > 0) {
          const authorLinks = selectedAuthors.map((author, index) => ({
            scientific_name_id: form.id,
            researcher_id: author.id,
            author_order: index + 1,
          }));
          await supabase.from("scientific_name_and_author").insert(authorLinks);
        }

        await upsertTypeHostRel(form.id);
        await syncEcologyTags(form.id);

        await writeAudit({
          action: "update",
          id: form.id,
          before: beforeRow || null,
          after: { ...(beforeRow || {}), ...updatePayload },
          actor: authUser
            ? { id: authUser.id, display: authUser.user_metadata?.display_name || authUser.email || null }
            : null,
        });
      }

      if (shouldClose) {
        setSuccessMsg(isEdit ? "Updated successfully." : "Added successfully.");
        setSuccessOpen(true);
      } else {
        alert("Core Taxonomy saved.");
      }
    } catch (e) {
      console.error(`${TAG} submit failed`, e);
      alert(`Error: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  }, [
    authUser,
    errors,
    form,
    imgRows,
    isEdit,
    selectedAuthors,
    form.id,
    scientificName?.id,
    typeTaxaRadio,
    upsertTypeHostRel,
    syncEcologyTags,
    saving,
  ]);

  // ---------- delete ----------
  const handleDelete = useCallback(async () => {
    if (localMode || !isEdit || !scientificName?.id) return;
    try {
      const { data: beforeRow } = await supabase
        .from("scientific_names")
        .select("*")
        .eq("id", scientificName.id)
        .maybeSingle();

      const { error } = await supabase.from("scientific_names").delete().eq("id", scientificName.id);
      if (error) throw new Error(error.message);

      await writeAudit({
        action: "delete",
        id: scientificName.id,
        before: beforeRow || null,
        after: null,
        actor: authUser
          ? { id: authUser.id, display: authUser.user_metadata?.display_name || authUser.email || null }
          : null,
      });

      alert("Deleted.");
      onClose?.(true);
    } catch (e) {
      console.error(`${TAG} delete failed`, e);
      alert(`Delete failed: ${e.message || e}`);
    }
  }, [authUser, isEdit, onClose, scientificName?.id]);

  return (
    <BaseDialog
      open={open}
      title={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {isEdit ? "Editing" : "Adding"}
          </Typography>
          <Typography variant="subtitle1" sx={{ lineHeight: 1.4 }}>
            <Box
              component="span"
              sx={{ fontWeight: 600, color: editingSummary.parentMissing ? "error.main" : "inherit" }}
            >
              {editingSummary.parentLabel}
            </Box>{" "}
            →{" "}
            <Box
              component="span"
              sx={{ fontWeight: 800, color: editingSummary.nameMissing ? "error.main" : "inherit" }}
            >
              {editingSummary.nameLabel}
            </Box>{" "}
            /{" "}
            <Box
              component="span"
              sx={{ fontWeight: 600, color: editingSummary.authorsMissing ? "error.main" : "inherit" }}
            >
              {editingSummary.authorsLabel}
            </Box>{" "}
            (
            <Box
              component="span"
              sx={{ fontWeight: 600, color: editingSummary.yearMissing ? "error.main" : "inherit" }}
            >
              {editingSummary.yearLabel}
            </Box>
            )
          </Typography>
        </Box>
      }
      headerAddon={
        <Box sx={{ px: 2, py: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ borderBottom: 1, borderColor: "divider" }}
          >
            {computedTabs.map(t => {
              const hasError = tabErrorMap[t.key];
              const isCoreAndNew = t.key === "core_taxonomy" && !isEdit;
              const iconColor = isCoreAndNew ? "error.main" : (hasError ? "error.main" : "inherit");
              const iconWithColor = React.cloneElement(t.icon, { sx: { color: iconColor } });
              return (
                <Tooltip title={t.label} key={t.key}>
                  <Tab
                    value={t.key}
                    icon={iconWithColor}
                  />
                </Tooltip>
              );
            })}
          </Tabs>
        </Box>
      }
      onClose={() => onClose?.(false)}
      onSubmit={handleSubmit}
      submitText={isEdit ? "Update" : "Add"}
      loading={loadingMaster || saving}
      onDelete={!localMode && isEdit ? handleDelete : undefined}
      maxWidth="md"
    >
      {/* ロード完了後に Section 群を描画しないとタイミング問題回避用 */}
      <Box sx={{ height: "75vh", overflowY: "auto" }}>
        {loadingMaster ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2">Loading master data…</Typography>
          </Box>
        ) : (
          <Box sx={{ px: 2 }}>
            <TabPanel current={activeTab} value="core_taxonomy">
              {/* Classification Section */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Classification</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditTaxonomyOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ bgcolor: 'action.hover', width: '25%' }} />
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', bgcolor: 'action.hover' }}>Rank</TableCell>
                          <TableCell sx={{ fontWeight: 700, color: 'text.secondary', bgcolor: 'action.hover' }}>Parent</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: 'text.secondary', bgcolor: 'action.hover' }}>Current</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.current_rank || "—"}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {form.current_parent ? lookupSciLabel(form.current_parent) : "—"}
                          </TableCell>
                        </TableRow>
                        {(form.original_rank || form.original_parent) && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: 'text.secondary', bgcolor: 'action.hover' }}>Original</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{form.original_rank || "—"}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {form.original_parent ? lookupSciLabel(form.original_parent) : "—"}
                            </TableCell>
                          </TableRow>
                        )}
                        {isInvalidRank(form.current_rank) && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: 'text.secondary', bgcolor: 'action.hover' }}>Status</TableCell>
                            <TableCell colSpan={2} sx={{ fontWeight: 600, color: 'error.main' }}>
                              Invalid ({form.current_invalid_type || "—"})
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>

              {/* Name Spelling & Authority Section */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Name Spelling & Authority</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditNameDetailsOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Valid Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.name_spell_valid || "—"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Original Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.name_spell_original || "—"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Authors</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {selectedAuthors.length > 0
                              ? selectedAuthors.map(a => a.last_name).join(', ')
                              : "—"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Year</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.authority_year || "—"}</TableCell>
                        </TableRow>
                        {form.zoobank_url && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>ZooBank</TableCell>
                            <TableCell sx={{ fontWeight: 600, wordBreak: 'break-all' }}>{form.zoobank_url}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>

              {/* Type Information Section */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Type Information</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditTypeInfoOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        {showTypeSpecimen ? (
                          <>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Category</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.type_category || "—"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Sex</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.type_sex || "—"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Locality</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.type_locality || "—"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Repository</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>
                                {form.type_repository_id
                                  ? (scientificName?.type_repository_id === form.type_repository_id
                                      ? (scientificName.type_repository || form.type_repository_id)
                                      : form.type_repository_id)
                                  : "—"}
                              </TableCell>
                            </TableRow>
                          </>
                        ) : (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Type Taxon</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {typeTaxaRadio === 'own'
                                ? "This name"
                                : (form.type_taxa_id ? lookupSciLabel(form.type_taxa_id) : "—")}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Remarks</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{form.type_remarks || "—"}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>

              {/* Original Description Source Section */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Original Description</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditSourceOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {form.source_of_original_description
                              ? renderPublicationLabel(publications.find(p => p.id === form.source_of_original_description)) || form.source_of_original_description
                              : "—"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Page</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.page || "—"}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>

              {/* Extant / Fossil Section */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Extant / Fossil</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditExtantFossilOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{form.extant_fossil || "unknown"}</TableCell>
                        </TableRow>
                        {form.extant_fossil === 'fossil' && (
                          <>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Type</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.fossil_type || "—"}</TableCell>
                            </TableRow>
                            {form.fossil_type === 'amber' && (
                              <TableRow>
                                <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Amber Group</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{form.amber_group || "—"}</TableCell>
                              </TableRow>
                            )}
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Era / Stratigraphy</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.geological_era || "—"}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Age (MYA)</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{form.fossil_age_mya || "—"}</TableCell>
                            </TableRow>
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>

              <Box sx={{ mt: 4, mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color={!isEdit ? "error" : "primary"} 
                  startIcon={<SaveIcon />}
                  onClick={() => handleSubmit(false)}
                  sx={{ minWidth: 200 }}
                >
                  Save Core Taxonomy
                </Button>
              </Box>
            </TabPanel>

            <TabPanel current={activeTab} value="occurrence">
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Occurrences
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddOccurrence} disabled={!isEdit}>
                    Add Occurrence
                  </Button>
                </Stack>
                {!isEdit && <Typography variant="caption" color="text.secondary">Save the name first to add occurrences.</Typography>}
                <Stack spacing={1}>
                  {occurrences.map(dist => {
                    const countryName = countriesList.find(c => c.id === dist.country_id)?.name_english || dist.country_id;
                    const recordTypeLabels = {
                      literature_only: 'Literature',
                      specimen_based_literature: 'Specimen (Lit.)',
                      specimen_record: 'Specimen',
                    };
                    const recordTypeLabel = recordTypeLabels[dist.record_type] || dist.record_type;
                    const validityLabel = dist.validity || 'unassessed';
                    const validityColor = {
                        valid: 'success',
                        invalid: 'error',
                        doubtful: 'warning',
                    }[validityLabel] || 'default';
                    return (
                    <Card key={dist.id} variant="outlined">
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box>
                            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                              {dist.is_type_locality && <Tooltip title="Type Locality"><LocationOnIcon color="primary" fontSize="small" /></Tooltip>}
                              <Tooltip title={`Validity: ${validityLabel}`}>
                                <Chip label={validityLabel} size="small" color={validityColor} variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Tooltip>
                              <Tooltip title={`Record Type: ${recordTypeLabel}`}>
                                <Chip label={recordTypeLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              </Tooltip>
                              <Typography variant="body2" fontWeight="bold">
                                {dist.locality ? `${countryName}: ${dist.locality}` : countryName}
                              </Typography>
                            </Stack>
                            {(dist.latitude || dist.longitude || dist.altitude) && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                                Lat: {dist.latitude ?? 'N/A'}, Lon: {dist.longitude ?? 'N/A'}
                                {dist.altitude != null && `, Alt: ${dist.altitude}m`}
                              </Typography>
                            )}
                            {dist.publication && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                Source: {formatPublicationCitation(dist.publication, dist.page)}
                              </Typography>
                            )}
                            {dist.remarks && (
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Note: {dist.remarks}
                              </Typography>
                            )}
                          </Box>
                          <Stack direction="row">
                            <IconButton size="small" onClick={() => handleEditOccurrence(dist)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteOccurrence(dist.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                    );
                  })}
                  {isEdit && occurrences.length === 0 && <Typography variant="body2" color="text.secondary">No occurrence records.</Typography>}
                </Stack>
              </Box>
            </TabPanel>

            <TabPanel current={activeTab} value="nomenclatural_acts">
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Nomenclatural Acts
                  </Typography>
                  <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddAct} disabled={!isEdit}>
                    Add Act
                  </Button>
                </Stack>
                {!isEdit && <Typography variant="caption" color="text.secondary">Save the name first to add acts.</Typography>}
                <Stack spacing={1}>
                  {taxonomicActs.map(act => {
                    const isSubject = act.scientific_name_id === form.id;
                    const subjectName = isSubject ? (form.name_spell_valid || "This Name") : (act.scientific_name?.name_spell_valid || "Unknown");
                    const objectName = isSubject ? (act.related_name?.name_spell_valid || "Unknown") : (form.name_spell_valid || "This Name");

                    return (
                      <Card key={act.id} variant="outlined">
                        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                <Typography variant="body2" fontWeight="bold">
                                  {subjectName}
                                </Typography>
                                <Chip
                                  label={act.act_type?.name || "Act"}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                  {objectName}
                                </Typography>
                              </Stack>

                              {act.publication && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {formatPublicationCitation(act.publication, act.page)}
                                </Typography>
                              )}
                              {act.remarks && (
                                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                  Note: {act.remarks}
                                </Typography>
                              )}
                            </Box>
                            <Stack direction="row">
                              <IconButton size="small" onClick={() => handleEditAct(act)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDeleteAct(act.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })}
                  {isEdit && taxonomicActs.length === 0 && (
                    <Typography variant="body2" color="text.secondary">No acts recorded.</Typography>
                  )}
                </Stack>
              </Box>
            </TabPanel>

            <TabPanel current={activeTab} value="ecology">
              <TypeHostSelector value={typeHostSelection} onChange={setTypeHostSelection} />
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Ecological Tags
              </Typography>
              <Autocomplete
                multiple
                options={ecologyTags}
                getOptionLabel={(o) => o?.name || ""}
                value={ecologyTags.filter((t) => selectedEcologyTags.includes(t.id))}
                onChange={(_, val) => setSelectedEcologyTags(val.map((v) => v.id))}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option.name || option.id} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select ecological tags"
                    placeholder="Select tags (multiple)"
                    fullWidth
                  />
                )}
              />
            </TabPanel>

            <TabPanel current={activeTab} value="gbif">
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>GBIF Information</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditGbifOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ width: '30%', color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Usage Keys</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {gbifUsageKeysValue?.length > 0 ? gbifUsageKeysValue.join(", ") : "—"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Associated IDs</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {mergedGbifIdsValue?.length > 0 ? mergedGbifIdsValue.join(", ") : "—"}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            </TabPanel>

            <TabPanel current={activeTab} value="other">
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Other Information</Typography>
                  <Button variant="outlined" size="small" startIcon={<EditIcon />} onClick={() => setEditOtherOpen(true)}>
                    Edit
                  </Button>
                </Stack>
                <Card variant="outlined" sx={{ overflow: 'hidden' }}>
                  <TableContainer>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid rgba(224, 224, 224, 0.5)', py: 1 } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Taxonomic Note</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{form.taxonomic_note || "—"}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ color: 'text.secondary', fontWeight: 500, bgcolor: 'action.hover' }}>Remark</TableCell>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'pre-wrap' }}>{form.remark || "—"}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </Box>
            </TabPanel>
          </Box>
        )}
      </Box>

      <Dialog open={gbifResultsOpen} onClose={() => setGbifResultsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>GBIF 検索結果（GBIF ID を選択）</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List dense>
            {gbifResults.map((hit) => (
              <ListItemButton
                key={hit.key}
                alignItems="flex-start"
                onClick={() => {
                  const newIds = Array.from(new Set([...form.merged_gbif_ids, String(hit.key)]));
                  handleChange("merged_gbif_ids", newIds);
                  setGbifResultsOpen(false);
                }}
              >
                <ListItemText
                  primary={hit.scientificName || hit.canonicalName || ""}
                  secondary={
                    <span>
                      {hit.authorship || ""} <br />
                      {hit.rank || ""} {hit.family ? ` / ${hit.family}` : ""}
                    </span>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGbifResultsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sub-dialogs for Core Taxonomy editing */}
      <Dialog open={editTaxonomyOpen} onClose={() => setEditTaxonomyOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Classification</DialogTitle>
        <DialogContent dividers>
          <SectionTaxonomy
            form={form}
            handleChange={handleChange}
            errors={{
              currentRank: errors.currentRank,
              current_parent: errors.current_parent,
              currentInvalidType: errors.currentInvalidType,
              currentInvalidHomonym: errors.currentInvalidHomonym,
              originalInvalidType: errors.originalInvalidType,
              originalInvalidHomonym: errors.originalInvalidHomonym,
            }}
            ranks={ranks}
            allowedRanks={AllowedRanks}
            invalidTypeOptions={invalidTypeOptions}
            scientificNameData={safeScientificNameData}
            refresh={refresh}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTaxonomyOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editNameDetailsOpen} onClose={() => setEditNameDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Name Details</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Spelling</Typography>
              <SectionNameSpelling
                form={form}
                handleChange={handleChange}
                errors={{ nameSpell: !form.name_spell_valid?.trim() }}
              />
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Authority</Typography>
              <SectionAuthority
                form={form}
                handleChange={handleChange}
                errors={{
                  authority: selectedAuthors.length === 0,
                  authorityYear: (() => {
                    const y = toStr(form.authority_year).trim();
                    return !y || !/^\d{4}$/.test(y);
                  })(),
                }}
                selectedAuthors={selectedAuthors}
                setSelectedAuthors={setSelectedAuthors}
                handleAuthorRemove={handleAuthorRemove}
                yearOptions={yearOptions}
              />
            </Box>
            <Divider />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>ZooBank</Typography>
              <SectionZooBank
                form={form}
                handleChange={handleChange}
                errors={{
                  zoobankUrl:
                    !!form.zoobank_url && !/^https?:\/\/(www\.)?zoobank\.org\/.+$/i.test(form.zoobank_url),
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNameDetailsOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editTypeInfoOpen} onClose={() => setEditTypeInfoOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Type Information</DialogTitle>
        <DialogContent dividers>
          {showTypeSpecimen ? (
            <SectionTypeSpecimen
              form={form}
              handleChange={handleChange}
              errors={{ typeImg: imgRowsInvalid }}
              typeCategories={typeCategories}
              onShowAddTypeCategory={undefined}
              countriesList={countriesList}
              RanksNoLocalityConst={RanksNoLocalityConst}
              imgRows={imgRows}
              handleImgRowChange={setImg}
              addImgRow={addImgRow}
              removeImgRow={removeImgRow}
            />
          ) : (
            <SectionTypeTaxon
              form={form}
              handleChange={handleChange}
              errors={{ typeTaxaId: errors.typeTaxaId }}
              typeTaxaRadio={typeTaxaRadio}
              handleTypeTaxaRadio={handleTypeTaxaRadio}
              refresh={refresh}
            />
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Type Remarks"
              value={form.type_remarks || ""}
              onChange={(e) => handleChange("type_remarks", e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTypeInfoOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editSourceOpen} onClose={() => setEditSourceOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Original Description Source</DialogTitle>
        <DialogContent dividers>
          <SectionIdSource
            form={form}
            handleChange={handleChange}
            publications={publications}
            renderPublicationLabel={renderPublicationLabel}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSourceOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editExtantFossilOpen} onClose={() => setEditExtantFossilOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Extant / Fossil Status</DialogTitle>
        <DialogContent dividers>
          <SectionExtantFossil form={form} handleChange={handleChange} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditExtantFossilOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editGbifOpen} onClose={() => setEditGbifOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit GBIF Information</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              GBIF Usage Keys
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]} // Allow free text input
              value={gbifUsageKeysValue}
              onChange={(_, newValue) => handleGbifUsageKeyChange(newValue)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add GBIF Usage Keys"
                  placeholder="Add GBIF Usage Keys (Enter to add)"
                  fullWidth
                  size="small"
                  error={errors.gbifUsageKeys}
                  helperText={
                    errors.gbifUsageKeys
                      ? "GBIF Usage Keyには数字のみを入力してください。"
                      : "複数のGBIF Backbone usageKeyを入力（Enterで追加）"
                  }
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Associated GBIF IDs
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
              <TextField
                label="検索用の学名"
                value={gbifSearchText}
                onChange={(e) => setGbifSearchText(e.target.value)}
                fullWidth
                size="small"
                helperText="検索に用いる学名を入力（編集可）"
              />
              <Autocomplete
                multiple
                freeSolo
                options={[]} // Allow free text input
                value={mergedGbifIdsValue}
                onChange={(_, newValue) => handleMergedGbifIdsChange(newValue)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip variant="outlined" label={option} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associated GBIF IDs"
                    placeholder="Add Associated GBIF IDs (Enter to add)"
                    fullWidth
                    size="small"
                    helperText="複数のGBIF IDを入力（Enterで追加）"
                  />
                )}
              />

              <Button
                variant="contained"
                onClick={handleGbifSearch}
                disabled={gbifSearching || !gbifSearchText || gbifSearchText.length < 3}
              >
                {gbifSearching ? "Searching..." : "GBIFで検索"}
              </Button>
            </Stack>
            {gbifSearchError ? (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {gbifSearchError}
              </Typography>
            ) : null}
            <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
              種・亜種は有効名スペルを基に検索します。属以上も同様に有効名スペルで検索します。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGbifOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOtherOpen} onClose={() => setEditOtherOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Edit Other Information</DialogTitle>
        <DialogContent dividers>
          <SectionOtherInfo form={form} handleChange={handleChange} statuses={statuses} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOtherOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={successOpen} onClose={() => { setSuccessOpen(false); onClose?.(true); }}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>{successMsg || "Saved successfully."}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuccessOpen(false); onClose?.(true); }} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <DialogTaxonomicActEditor
        open={actEditorOpen}
        onClose={() => setActEditorOpen(false)}
        onSave={handleActSaved}
        act={targetAct}
        currentNameId={form.id}
        currentNameLabel={form.name_spell_valid}
        publications={publications}
      />

      <DialogOccurrenceEditor
        open={occurrenceEditorOpen}
        onClose={() => setOccurrenceEditorOpen(false)}
        onSave={handleOccurrenceSaved}
        occurrence={targetOccurrence}
        scientificNameId={form.id}
        publications={publications}
        countries={countriesList}
      />
    </BaseDialog>
  );
}
